/* Shared motion primitives for the /work pages.
 * Everything honors prefers-reduced-motion and uses the studio easing.
 */
import React, { useEffect, useRef, useState } from "react";
import {
  motion,
  useInView,
  useReducedMotion,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import Lenis from "lenis";

/** Studio easing — expo-out. Used everywhere for the "settled" premium feel. */
export const EASE = [0.16, 1, 0.3, 1];

/* ─── Smooth scroll ─────────────────────────────────────────────────────── */

/** Lenis smooth scroll for a page. Call once at the page root. */
export const useLenis = () => {
  const reduced = useReducedMotion();
  useEffect(() => {
    if (reduced) return undefined;
    // Lenis and CSS smooth-scroll fight each other.
    const html = document.documentElement;
    const prev = html.style.scrollBehavior;
    html.style.scrollBehavior = "auto";

    const lenis = new Lenis({ lerp: 0.1, smoothWheel: true });
    let raf = requestAnimationFrame(function loop(t) {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    });
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      html.style.scrollBehavior = prev;
    };
  }, [reduced]);
};

/* ─── Text reveals ──────────────────────────────────────────────────────── */

/**
 * Oversized display text that rises line-by-line out of a clip mask.
 * Pass `lines` as an array — each entry is one visual line.
 *
 *   <RiseLines lines={["Software", "that ships."]} as="h1" className="..." />
 */
/* ─── Reveal-on-scroll hook ───────────────────────── */

/**
 * Latches shown true the first time the element is in (or near) the viewport,
 * from EITHER framer's IntersectionObserver OR a native scroll/resize check.
 * The native fallback (real scroll events + getBoundingClientRect) can't be
 * dropped the way a lone whileInView enter can — under Lenis smooth scroll,
 * momentum scrolling, or bfcache restores — so reveal-animated content is never
 * stranded in its hidden initial state (clipped words, opacity 0, …).
 */
const useInViewReveal = (amount = 0.3) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount, margin: "0px 0px -10% 0px" });
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (reduced) {
      setReady(true);
      return undefined;
    }
    let done = false;
    const check = () => {
      const el = ref.current;
      if (done || !el) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        done = true;
        setReady(true);
      }
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [reduced]);

  return { ref, shown: reduced || inView || ready, reduced };
};

export const RiseLines = ({
  lines,
  as: Tag = "h1",
  className,
  style,
  delay = 0,
}) => {
  const { ref, shown, reduced } = useInViewReveal(0.6);
  return (
    <Tag ref={ref} className={className} style={style} aria-label={lines.join(" ")}>
      {lines.map((line, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{ display: "block", overflow: "hidden" }}
        >
          <motion.span
            style={{ display: "block", willChange: "transform" }}
            initial={false}
            animate={{ y: shown ? "0%" : "110%" }}
            transition={{ duration: reduced ? 0 : 0.9, ease: EASE, delay: shown ? delay + i * 0.09 : 0 }}
          >
            {line}
          </motion.span>
        </span>
      ))}
    </Tag>
  );
};

/** Word-by-word rise for paragraphs and quotes. */
export const RiseWords = ({ text, className, style, delay = 0, stagger = 0.018 }) => {
  const { ref, shown, reduced } = useInViewReveal(0.4);
  const words = String(text || "").split(/\s+/).filter(Boolean);
  return (
    <span ref={ref} className={className} style={style} aria-label={text}>
      {words.map((w, i) => (
        <span
          key={i}
          aria-hidden="true"
          style={{ display: "inline-block", overflow: "hidden", verticalAlign: "bottom" }}
        >
          <motion.span
            style={{ display: "inline-block" }}
            initial={false}
            animate={{ y: shown ? "0%" : "110%" }}
            transition={{ duration: reduced ? 0 : 0.7, ease: EASE, delay: shown ? delay + i * stagger : 0 }}
          >
            {w}
          </motion.span>
          {i < words.length - 1 ? " " : ""}
        </span>
      ))}
    </span>
  );
};

/* ─── Block reveals ─────────────────────────────────────────────────────── */

/** Fade + rise for any block as it enters the viewport. */
export const Reveal = ({ children, delay = 0, y = 36, className, style, amount = 0.3 }) => {
  const { ref, shown, reduced } = useInViewReveal(amount);
  return (
    <motion.div
      ref={ref}
      className={className}
      style={style}
      initial={false}
      animate={{ opacity: shown ? 1 : 0, y: shown ? 0 : y }}
      transition={{ duration: reduced ? 0 : 0.8, ease: EASE, delay: shown ? delay : 0 }}
    >
      {children}
    </motion.div>
  );
};

/** Image that un-masks (clip-path) and settles from a slight zoom.
 *
 * Reveal is driven by an explicit `useInView` + a real scroll/resize fallback
 * rather than framer's `whileInView`. On mobile Safari, momentum ("fling")
 * scrolling and bfcache restores can drop the single `whileInView` enter
 * callback — and because the closed state (`inset(0 0 100%)`) fully clips the
 * image, a missed callback left every photo permanently invisible. Latching
 * `shown` open from either signal guarantees an image is never stuck hidden,
 * while still playing the un-mask animation when it scrolls into view. */
export const MaskImage = ({ src, alt = "", ratio, className, style, delay = 0 }) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, {
    once: true,
    amount: 0.15,
    margin: "0px 0px -10% 0px",
  });
  const [ready, setReady] = useState(false);

  // Fail-safe: reveal any image whose box is within the viewport on a real
  // scroll/resize. Uses native scroll events (which always fire on touch) +
  // getBoundingClientRect, so it can't miss the way the IO enter can.
  useEffect(() => {
    if (reduced) {
      setReady(true);
      return undefined;
    }
    let done = false;
    const check = () => {
      const el = ref.current;
      if (done || !el) return;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        done = true;
        setReady(true);
      }
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    window.addEventListener("resize", check);
    return () => {
      window.removeEventListener("scroll", check);
      window.removeEventListener("resize", check);
    };
  }, [reduced]);

  const shown = reduced || inView || ready;

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ overflow: "hidden", aspectRatio: ratio, ...style }}
      initial={false}
      animate={{ clipPath: shown ? "inset(0 0 0% 0)" : "inset(0 0 100% 0)" }}
      transition={{ duration: reduced ? 0 : 1.1, ease: EASE, delay: shown ? delay : 0 }}
    >
      <motion.img
        src={src}
        alt={alt}
        loading="lazy"
        style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        initial={false}
        animate={{ scale: shown ? 1 : 1.18 }}
        transition={{ duration: reduced ? 0 : 1.4, ease: EASE, delay: shown ? delay : 0 }}
      />
    </motion.div>
  );
};

/** Subtle scroll parallax wrapper (translateY between -range..range px). */
export const Parallax = ({ children, range = 60, className, style }) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [range, -range]);
  return (
    <motion.div ref={ref} className={className} style={{ y: reduced ? 0 : y, ...style }}>
      {children}
    </motion.div>
  );
};

/* ─── Numbers ───────────────────────────────────────────────────────────── */

/**
 * Animated stat. Parses strings like "+120% revenue", "Sub-1s checkout",
 * "8 wks to launch" — the first number counts up, prefix/suffix stay put.
 */
export const StatCounter = ({ value, className, style, duration = 1.4 }) => {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.6 });
  const match = String(value).match(/(-?\d+(?:[.,]\d+)?)/);
  const [display, setDisplay] = useState(match ? "0" : String(value));

  useEffect(() => {
    if (!match) return undefined;
    const target = parseFloat(match[1].replace(",", "."));
    if (reduced || !inView) {
      if (reduced) setDisplay(match[1]);
      return undefined;
    }
    const t0 = performance.now();
    const dec = (match[1].split(/[.,]/)[1] || "").length;
    let raf = requestAnimationFrame(function tick(t) {
      const p = Math.min(1, (t - t0) / (duration * 1000));
      const eased = 1 - Math.pow(1 - p, 4); // expo-ish out
      setDisplay((target * eased).toFixed(dec));
      if (p < 1) raf = requestAnimationFrame(tick);
    });
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, reduced]);

  if (!match) {
    return (
      <span ref={ref} className={className} style={style}>
        {value}
      </span>
    );
  }
  const [prefix, , suffix] = [
    String(value).slice(0, match.index),
    match[1],
    String(value).slice(match.index + match[1].length),
  ];
  return (
    <span ref={ref} className={className} style={style}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

/* ─── Chrome ────────────────────────────────────────────────────────────── */

/** Red hairline reading-progress bar pinned to the top of the viewport. */
export const ProgressBar = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 140, damping: 26, mass: 0.4 });
  return (
    <motion.div
      aria-hidden="true"
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        height: 2,
        background: "var(--fw-red)",
        transformOrigin: "0% 50%",
        scaleX,
        zIndex: 90,
      }}
    />
  );
};

/** Endless marquee row — pass children once, it duplicates for the loop. */
export const Marquee = ({ children, speed = 26, className }) => {
  const reduced = useReducedMotion();
  if (reduced) {
    return (
      <div className={className} style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        {children}
      </div>
    );
  }
  return (
    <div className={className} style={{ overflow: "hidden", display: "flex" }}>
      {[0, 1].map((n) => (
        <motion.div
          key={n}
          aria-hidden={n === 1}
          style={{ display: "flex", flexShrink: 0, alignItems: "center" }}
          animate={{ x: "-100%" }}
          transition={{ duration: speed, ease: "linear", repeat: Infinity }}
        >
          {children}
        </motion.div>
      ))}
    </div>
  );
};

/* ─── Color helpers ─────────────────────────────────────────────────────── */

/** True when a hex color reads as dark → light text on top. */
export const isDark = (hex) => {
  const h = String(hex || "#1f1f1f").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const r = parseInt(full.slice(0, 2), 16) || 0;
  const g = parseInt(full.slice(2, 4), 16) || 0;
  const b = parseInt(full.slice(4, 6), 16) || 0;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255 < 0.55;
};
